import {
    GLAutoBookingModel,
    CompanyEditModel,
    CompanyModel,
    CurrencyModel,
    GLAccountModel,
    GLReportHeaderModel,
    GLReportHeaderWithItems,
    GLReportItemModel,
    GLReportLineModel,
    GLReportModel,
    GeneralLedgerExtended,
    GeneralLedgerModel,
    GlDocumentModel,
    GlDocumentWithItems,
    GlReportLineExtended,
    PaginatedDataGLDoc,
    TypeAndClassModel,
    PaginatedData,
} from '@common/modelDefinition';
import rest from '@data/restWrapper';
import { ExchangeRates } from '@pages/checkoutAndInvoice/checkout/interfaces';

export const getExcelFileForAccounting = async (
    data: GLReportLineModel[],
    firstRowHeaders: string[],
    secondRowHeaders: string[],
    cellsToMerge: string[]
): Promise<any> => {
    const res = await rest('/getExcelFileForAccounting', 'POST', {
        data,
        firstRowHeaders,
        secondRowHeaders,
        cellsToMerge,
    });
    const base64 = res.file;
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'hotelExport.xlsx'; // Naziv fajla koji će se preuzeti
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

export const getAllCompanies = async (): Promise<CompanyEditModel[]> => {
    return await rest('/Company', 'GET');
};

export const getCompany = async (companyId: number): Promise<CompanyModel> => {
    return await rest('/Company/' + companyId);
};

export const getAllTypesAndClasses = async (): Promise<TypeAndClassModel[]> => {
    return await rest('/TypeAndClass', 'GET');
};

export const createTypeAndClass = async (typeAndClass: TypeAndClassModel): Promise<TypeAndClassModel> => {
    return await rest('/TypeAndClass', 'POST', typeAndClass);
};

export const editTypeAndClass = async (typeAndClass: TypeAndClassModel): Promise<TypeAndClassModel> => {
    return await rest('/TypeAndClass/' + typeAndClass?.id, 'PUT', typeAndClass);
};

export const getAllCurrencies = async (): Promise<CurrencyModel[]> => {
    return await rest('/Currency', 'GET');
};

export const getExchangeRatesOnDay = async (): Promise<ExchangeRates> => {
    const latestExchangeRates = await rest('/getExchangeRatesOnDate/' + new Date().getTime());
    let _exchangeRates: ExchangeRates = {};
    latestExchangeRates.forEach((e: any) => {
        if (e.shortName) {
            _exchangeRates[e.currencyId] = e.exchangeRate;
        }
    });
    return _exchangeRates;
};

export const deleteGeneralLedger = async (glDocId: number): Promise<void> => {
    return await rest('/deleteActiveGeneralLedger/' + glDocId, 'POST');
};

export const generateYearlyEntryConclusion = async (
    reportForYear: number,
    glReportId: number
): Promise<GLReportHeaderModel> => {
    return await rest('/generateYearlyEntryConclusion/' + glReportId, 'POST', { reportForYear });
};

export const generateGLReport = async (
    reportForYear: number,
    reportForMonth: number,
    glReportId: number
): Promise<GLReportHeaderModel> => {
    return await rest('/generateGLReport/' + glReportId, 'POST', { reportForYear, reportForMonth });
};

export const closeYearlyGLReportData = async (glReportHeaderId: number): Promise<void> => {
    return await rest('/closeYearlyGLReportData/' + glReportHeaderId, 'POST');
};

export const getGLReportHeaders = async (page: number, rows: number): Promise<GLReportHeaderModel[]> => {
    return await rest('/getGLReportHeaders?page=' + page + '&rows=' + rows, 'GET');
};

export const getGLReportDataForDay = async (
    glReportId: number,
    from: number,
    to: number
): Promise<GLReportLineModel[]> => {
    return await rest('/getGLReportDataForDay/' + glReportId + '/' + from + '/' + to, 'GET');
};

export const getSelectedGLReportData = async (
    glReportId: number,
    year: number | null,
    month: number | null
): Promise<GLReportLineModel[]> => {
    return await rest('/getSelectedGLReportData/' + glReportId + '/' + year + '/' + month, 'GET');
};

export const checkIfGLAccountExist = async (glAccount: GLAccountModel): Promise<boolean> => {
    return await rest('/checkIfGLAccountExist', 'POST', { glAccount });
};

export const createOrEditGLAccounts = async (
    glAccounts: GLAccountModel[],
    edit?: boolean,
    importCSV?: boolean
): Promise<void> => {
    if (edit) {
        return await rest('/createOrEditGLAccounts', 'POST', { glAccounts, edit });
    } else {
        if (importCSV) {
            return await rest('/createOrEditGLAccounts', 'POST', { glAccounts, importCSV });
        } else {
            return await rest('/createOrEditGLAccounts', 'POST', { glAccounts });
        }
    }
};

export const getGLAccountsList = async (
    getChildElementsForRoot: string | null,
    getOnlyRoots?: boolean,
    account?: string | null,
    getLastChild?: boolean,
    getOnlyChilds?: boolean
): Promise<GLAccountModel[]> => {
    if (getOnlyRoots) {
        return await rest('/getGLAccountsList/' + (account ? account : null), 'POST', { getOnlyRoots });
    } else if (getChildElementsForRoot) {
        if (getLastChild) {
            return await rest('/getGLAccountsList/' + (account ? account : null), 'POST', {
                getChildElementsForRoot,
                getLastChild,
            });
        } else {
            return await rest('/getGLAccountsList/' + (account ? account : null), 'POST', { getChildElementsForRoot });
        }
    } else {
        if (getLastChild) {
            return await rest('/getGLAccountsList/' + (account ? account : null), 'POST', {
                getLastChild,
            });
        } else if (getOnlyChilds) {
            return await rest('/getGLAccountsList/' + (account ? account : null), 'POST', { getOnlyChilds });
        } else {
            return await rest('/getGLAccountsList/' + (account ? account : null), 'POST');
        }
    }
};

export const getGLReportDocuments = async (glReportId: number | null): Promise<GLReportModel[]> => {
    return await rest('/getGLReportDocuments/' + glReportId, 'GET');
};

export const getGLReportLine = async (glReportId: number): Promise<GlReportLineExtended> => {
    return await rest('/getGLReportLine/' + glReportId, 'GET');
};

export const createGLReportLine = async (lineText: string | null, glReportId: number): Promise<void> => {
    return await rest('/createGLReportLine', 'POST', { lineText, glReportId });
};

export const createOrEditGLReportDoc = async (glReportDoc: GLReportModel): Promise<GLReportModel> => {
    return await rest('/createOrEditGLReportDoc', 'POST', { glReportDoc });
};

export const editGLReportItems = async (glReportItems: GLReportItemModel[]): Promise<void> => {
    return await rest('/editGLReportItems', 'POST', { glReportItems });
};

export const getLockedOrUnlockedGLDocuments = async (
    page: number,
    rows: number,
    getLockedGLDoc?: boolean
): Promise<PaginatedData> => {
    if (getLockedGLDoc) {
        return await rest(
            '/getLockedOrUnlockedGLDocuments?getLockedGLDoc=' + getLockedGLDoc + '&page=' + page + '&rows=' + rows,
            'GET'
        );
    } else {
        return await rest('/getLockedOrUnlockedGLDocuments?page=' + page + '&rows=' + rows, 'GET');
    }
};

export const getGLDocumentWithItems = async (glDocId: number): Promise<GlDocumentWithItems> => {
    return await rest('/getGLDocumentWithItems/' + glDocId, 'GET');
};

export const getYearlyGLReportData = async (
    glReportId: number,
    year: number
): Promise<GLReportHeaderWithItems | null> => {
    return await rest('/getYearlyGLReportData/' + glReportId + '/' + year, 'GET');
};

export const getGLDocumentNumSuggestion = async (glClass: number | null): Promise<string> => {
    return await rest('/getGLDocumentNumSuggestion/' + glClass, 'GET');
};

export const checkIfGLDocumentNumberExist = async (
    glDocId: number | null,
    docNumber: string,
    classId: number | null
): Promise<boolean> => {
    return await rest('/checkIfGLDocumentNumberExist/' + glDocId + '/' + docNumber + '/' + classId, 'GET');
};

export const checkAllowEditConcludedDocument = async (): Promise<boolean> => {
    return await rest('/checkAllowEditConcludedDocument', 'GET');
};

export const saveOrEditGlDocument = async (glDocument: GlDocumentModel): Promise<GlDocumentModel> => {
    return await rest('/saveOrEditGlDocument', 'POST', { glDocument });
};

export const getPrevOrNextGLDocument = async (type: string, selectedId: number): Promise<GlDocumentModel> => {
    return await rest('/getPrevOrNextGLDocument/' + selectedId + '/' + type, 'GET');
};

export const getGLDocByAccountAndPeriod = async (
    account: string,
    page: number,
    rows: number,
    from: number,
    to: number
): Promise<PaginatedDataGLDoc> => {
    return await rest(`/getGLDocByAccountAndPeriod?account=${account}&page=${page}&rows=${rows}&from=${from}&to=${to}`);
};

export const getAnalyticalCardsOrPartnersBalance = async (
    account: string,
    page: number,
    rows: number,
    companyId?: number | null,
    from?: number | null,
    to?: number | null
): Promise<PaginatedDataGLDoc> => {
    return await rest(
        `/getAnalyticalCardsOrPartnersBalance?account=${account}&page=${page}&rows=${rows}&companyId=${
            companyId ? companyId : null
        }&from=${from ? from : null}&to=${to ? to : null}`
    );
};

export const searchGLAccounts = async (
    searchString: string,
    analytic?: boolean,
    exactAccount?: boolean
): Promise<GLAccountModel[]> => {
    if (exactAccount) {
        return await rest(`/searchGLAccounts?search=${searchString}&exactAccount=${exactAccount}`);
    } else if (analytic) {
        return await rest(`/searchGLAccounts?search=${searchString}&analytic=${analytic}`);
    } else {
        return await rest(`/searchGLAccounts?search=${searchString}`);
    }
};

export const getAutoGLBookings = async (): Promise<GLAutoBookingModel[]> => {
    return await rest('/getAutoGLBookings', 'GET');
};

export const getAutoBookingRules = async (): Promise<any> => {
    return await rest('/getAutoBookingRules', 'GET');
};

export const saveAutoGLBookings = async (editedItems: GLAutoBookingModel[]): Promise<{ data: GLAutoBookingModel[]; errors: Record<number, Record<string, boolean>> }> => {
    return await rest('/saveAutoGLBookings', 'POST', { editedItems });
};

export const saveGeneralLedgerItems = async (
    glDocumentId: number,
    newGLItems: GeneralLedgerModel[],
    editedGLItems: GeneralLedgerExtended[]
): Promise<GlDocumentWithItems> => {
    return await rest('/saveGeneralLedgerItems/' + glDocumentId, 'POST', { newGLItems, editedGLItems });
};
